import {useSelector, useDispatch} from 'react-redux';
import {
    selectChatHistory,
    setChatHistoryCurrentPage,
    setChatHistoryFilterParams,
    setChatHistoryDateLabelKey,
    setChatHistoryFilterUserInteracted,
    fetchChatHistory
} from '../../../features/sidekick/sidekickSlice';
import sidekickService from '../../../features/sidekick/sidekickService';
import {FunctionComponent, useEffect, useRef, useState} from 'react';
import {Button, Chip, IconButton, Select} from '@panviva/panviva-react-ui';
import DatePicker from 'react-datepicker';
import {CustomDateContainer, CustomDateInput} from './dateFilter';
import {DATE_PRESET_OPTIONS} from '../../../constants/sidekick';

interface FilterPanelProps {
    onClose?: (value: boolean) => void;
    onLookupFailure?: () => void;
}

type FilteredValues = {
    conversationCreatedAt_gte?: string;
    conversationCreatedAt_lte?: string;
    conversationFeedback?: string[];
    emailId?: string[];
    citation?: string;
};

type LookupStatus = 'idle' | 'loading' | 'success' | 'failure';

const FilterPanel: FunctionComponent<FilterPanelProps> = ({
    onClose,
    onLookupFailure
}) => {
    const dispatch = useDispatch<any>();

    const conversationFeedbackOptions = [
        {label: 'Helpful', value: 'Helpful'},
        {label: 'Not Helpful', value: 'Not Helpful'},
        {label: 'Skip (-)', value: 'Skip'}
    ];
    const chatHistoryState = useSelector(selectChatHistory);
    const [filteredValues, setFilteredValues] = useState<FilteredValues>(() => {
        // Convert filterParams from store to local filteredValues shape
        const params = chatHistoryState.filterParams || {};
        const initial: FilteredValues = {};
        // Use filterParams first, fall back to default from/to state values
        const fromValue =
            typeof params['from'] === 'string'
                ? params['from']
                : chatHistoryState.from;
        const toValue =
            typeof params['to'] === 'string'
                ? params['to']
                : chatHistoryState.to;
        if (fromValue) {
            initial['conversationCreatedAt_gte'] = fromValue;
        }
        if (toValue) {
            initial['conversationCreatedAt_lte'] = toValue;
        }
        if (Array.isArray(params['feedback'])) {
            initial['conversationFeedback'] = params['feedback'] as string[];
        }
        if (Array.isArray(params['emailId'])) {
            initial['emailId'] = params['emailId'] as string[];
        }
        if (typeof params['citation'] === 'string') {
            initial['citation'] = params['citation'];
        } else if (Array.isArray(params['citation'])) {
            initial['citation'] = (params['citation'][0] as string) || '';
        }
        return initial;
    });
    const datePickerRef = useRef<any>(null);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    // Local state is required to stage the label while the user interacts with the picker,
    // matching the same uncommitted pattern as filteredValues. The value is only written to
    // Redux (setChatHistoryDateLabelKey) after a successful Apply, so closing the panel
    // without applying does not persist a half-changed label.
    const userInteracted = chatHistoryState.filterUserInteracted;
    const [dateLabelKey, setDateLabelKey] = useState<string | null>(() => {
        if (chatHistoryState.dateLabelKey) return chatHistoryState.dateLabelKey;

        const params = chatHistoryState.filterParams || {};
        const hasExplicitFrom = typeof params['from'] === 'string';
        const hasExplicitTo = typeof params['to'] === 'string';

        return !hasExplicitFrom && !hasExplicitTo ? 'last30Days' : null;
    });
    const [emailIdOptions, setEmailIdOptions] = useState<string[]>([]);
    const [emailIdLoading, setEmailIdLoading] = useState(false);
    const [emailLookupStatus, setEmailLookupStatus] =
        useState<LookupStatus>('idle');
    const [emailIdInputValue, setEmailIdInputValue] = useState<string>('');
    const [citationOptions, setCitationOptions] = useState<string[]>([]);
    const [citationLoading, setCitationLoading] = useState(false);
    const [citationLookupStatus, setCitationLookupStatus] =
        useState<LookupStatus>('idle');
    const [citationInputValue, setCitationInputValue] = useState<string>(() => {
        const params = chatHistoryState.filterParams || {};
        if (typeof params['citation'] === 'string') return params['citation'];
        if (Array.isArray(params['citation']))
            return (params['citation'][0] as string) || '';
        return '';
    });
    const [resetAnnouncement, setResetAnnouncement] = useState('');
    const [emailIdAnnouncement, setEmailIdAnnouncement] = useState('');
    const [feedbackAnnouncement, setFeedbackAnnouncement] = useState('');

    const emailIdAbortRef = useRef<AbortController | null>(null);
    const citationAbortRef = useRef<AbortController | null>(null);
    const isSelectingEmailId = useRef(false);
    const isSelectingCitation = useRef(false);
    const skipCitationFetch = useRef<boolean>(
        !!(chatHistoryState.filterParams || {})['citation']
    );
    const filterPanelRef = useRef<HTMLDivElement>(null);
    const firstFocusableElementRef = useRef<HTMLElement | null>(null);
    const emailSelectOpen = useRef(false);
    const emailWrapperRef = useRef<HTMLDivElement>(null);
    const citationWrapperRef = useRef<HTMLDivElement>(null);
    const conversationFeedbackWrapperRef = useRef<HTMLDivElement>(null);

    // Auto-clear reset announcement after 4 seconds
    useEffect(() => {
        if (!resetAnnouncement) return;

        const timer = setTimeout(() => {
            setResetAnnouncement('');
        }, 4000);

        return () => clearTimeout(timer);
    }, [resetAnnouncement]);

    // Auto-clear emailId announcement after 3 seconds
    useEffect(() => {
        if (!emailIdAnnouncement) return;

        const timer = setTimeout(() => {
            setEmailIdAnnouncement('');
        }, 3000);

        return () => clearTimeout(timer);
    }, [emailIdAnnouncement]);

    // Auto-clear feedback announcement after 3 seconds
    useEffect(() => {
        if (!feedbackAnnouncement) return;

        const timer = setTimeout(() => {
            setFeedbackAnnouncement('');
        }, 3000);

        return () => clearTimeout(timer);
    }, [feedbackAnnouncement]);

    // Fetch email filter data with debounce and request cancellation
    useEffect(() => {
        if (!emailIdInputValue) {
            setEmailIdOptions([]);
            setEmailIdLoading(false);
            setEmailLookupStatus('idle');
            return;
        }

        setEmailIdLoading(true);
        setEmailLookupStatus('loading');

        const timer = setTimeout(async () => {
            // Cancel any prior in-flight request
            emailIdAbortRef.current?.abort();
            emailIdAbortRef.current = new AbortController();

            try {
                const response = await sidekickService.getFilterDataSet(
                    'email',
                    emailIdInputValue,
                    200,
                    emailIdAbortRef.current.signal
                );
                setEmailIdOptions(response.data ?? []);
                setEmailLookupStatus('success');
            } catch (error) {
                // Only update state if this request wasn't aborted
                if (
                    error instanceof Error &&
                    error.name !== 'AbortError' &&
                    error.name !== 'CanceledError'
                ) {
                    setEmailIdOptions([]);
                    setEmailLookupStatus('failure');
                    onLookupFailure?.();
                }
            } finally {
                setEmailIdLoading(false);
            }
        }, 300);

        return () => {
            clearTimeout(timer);
        };
    }, [emailIdInputValue]);

    // Fetch citation filter data with debounce and request cancellation
    useEffect(() => {
        if (!citationInputValue) {
            setCitationOptions([]);
            setCitationLoading(false);
            setCitationLookupStatus('idle');
            // Clear the selected citation when input is cleared
            setFilteredValues((prev) => ({...prev, citation: ''}));
            return;
        }

        // Skip fetch when citationInputValue was just set programmatically after selection
        if (skipCitationFetch.current) {
            skipCitationFetch.current = false;
            return;
        }

        setCitationLoading(true);
        setCitationLookupStatus('loading');

        const timer = setTimeout(async () => {
            // Cancel any prior in-flight request
            citationAbortRef.current?.abort();
            citationAbortRef.current = new AbortController();

            try {
                const response = await sidekickService.getFilterDataSet(
                    'citation',
                    citationInputValue,
                    200,
                    citationAbortRef.current.signal
                );
                setCitationOptions(response.data ?? []);
                setCitationLookupStatus('success');
            } catch (error) {
                // Only update state if this request wasn't aborted
                if (
                    error instanceof Error &&
                    error.name !== 'AbortError' &&
                    error.name !== 'CanceledError'
                ) {
                    setCitationOptions([]);
                    setCitationLookupStatus('failure');
                    onLookupFailure?.();
                }
            } finally {
                setCitationLoading(false);
            }
        }, 300);

        return () => {
            clearTimeout(timer);
        };
    }, [citationInputValue]);

    // AC-4: Auto-focus first element when filter panel opens
    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            if (datePickerRef.current) {
                const input = datePickerRef.current.input;
                if (input) {
                    input.focus();
                    firstFocusableElementRef.current = input;
                }
            }
        }, 100);

        return () => window.clearTimeout(timeoutId);
    }, []);

    // Set aria-describedby for filter inputs on mount only
    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            const emailInput =
                emailWrapperRef.current?.querySelector<HTMLInputElement>(
                    'input[data-testid="textfield-input"]'
                );
            if (emailInput && !emailInput.hasAttribute('aria-describedby')) {
                emailInput.setAttribute(
                    'aria-describedby',
                    'email-id-description'
                );
            }

            const feedbackInput =
                conversationFeedbackWrapperRef.current?.querySelector<HTMLInputElement>(
                    'input[data-testid="textfield-input"]'
                );
            if (
                feedbackInput &&
                !feedbackInput.hasAttribute('aria-describedby')
            ) {
                feedbackInput.setAttribute(
                    'aria-describedby',
                    'feedback-description'
                );
            }
        }, 100);

        return () => window.clearTimeout(timeoutId);
    }, []); // Empty deps array - run only on mount

    const getLookupTipText = (
        status: LookupStatus,
        defaultText: string,
        type?: 'citation' | 'emailId'
    ): string => {
        // error case is handled in error toaster implementation, so we don't show error text here
        if (status === 'loading') return 'Loading...';
        if (status === 'success') {
            if (type === 'citation') return 'No citations found';
            return 'No results found';
        }
        return defaultText;
    };

    const handleEmailIdType = (e: React.FormEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;
        // Only process events from text input elements, ignore buttons, checkboxes, etc.
        if (
            target.tagName !== 'INPUT' ||
            (target as HTMLInputElement).type !== 'text'
        ) {
            return;
        }
        const query = (target as HTMLInputElement).value;
        setEmailIdInputValue(query);
    };

    const handleCitationType = (e: React.FormEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;
        // Only process events from text input elements, ignore buttons, checkboxes, etc.
        if (
            target.tagName !== 'INPUT' ||
            (target as HTMLInputElement).type !== 'text'
        ) {
            return;
        }
        let query = (target as HTMLInputElement).value;

        // Only allow numbers - remove non-numeric characters
        query = query.replace(/\D/g, '');

        // Update the input value to only show numbers
        if ((target as HTMLInputElement).value !== query) {
            (target as HTMLInputElement).value = query;
        }

        setCitationInputValue(query);
    };

    const getArrayFilterValue = (
        value: string[] | string | undefined
    ): string[] => {
        return Array.isArray(value) ? value : [];
    };

    const getStringFilterValue = (
        value: string[] | string | undefined
    ): string => {
        return typeof value === 'string' ? value : '';
    };

    const handleApply = async () => {
        // Clear reset announcement if showing
        setResetAnnouncement('');

        const filterParams: Record<string, string | string[]> = {};
        const from = getStringFilterValue(
            filteredValues.conversationCreatedAt_gte
        );
        const to = getStringFilterValue(
            filteredValues.conversationCreatedAt_lte
        );
        const feedback = getArrayFilterValue(
            filteredValues.conversationFeedback
        );
        const emailId = getArrayFilterValue(filteredValues.emailId);
        const citation = getStringFilterValue(filteredValues.citation);
        if (from) filterParams['from'] = from;
        if (to) filterParams['to'] = to;
        if (feedback && feedback.length > 0)
            filterParams['feedback'] = feedback;
        if (emailId && emailId.length > 0) filterParams['emailId'] = emailId;
        if (citation) filterParams['citation'] = citation;
        const {rowsPerPage, sortField, sortDirection} = chatHistoryState;
        const resultAction = await dispatch(
            fetchChatHistory({
                page: 1,
                pageSize: rowsPerPage,
                sortBy: sortField,
                sortDirection: sortDirection,
                filters: filterParams,
                requestSource: 'filter'
            })
        );

        if (fetchChatHistory.fulfilled.match(resultAction)) {
            dispatch(setChatHistoryCurrentPage(1));
            dispatch(setChatHistoryFilterParams(filterParams));
            dispatch(setChatHistoryDateLabelKey(dateLabelKey));
            onClose?.(false);
        }
    };

    const handleReset = async () => {
        dispatch(setChatHistoryFilterUserInteracted(false));
        const {rowsPerPage, sortField, sortDirection, from, to} =
            chatHistoryState;
        setFilteredValues({
            conversationCreatedAt_gte: from || '',
            conversationCreatedAt_lte: to || '',
            conversationFeedback: [],
            emailId: [],
            citation: ''
        });
        setDateLabelKey('last30Days');
        // Clear input values and options
        setEmailIdInputValue('');
        setEmailIdOptions([]);
        setEmailLookupStatus('idle');
        setCitationInputValue('');
        setCitationOptions([]);
        setCitationLookupStatus('idle');
        const resultAction = await dispatch(
            fetchChatHistory({
                page: 1,
                pageSize: rowsPerPage,
                sortBy: sortField,
                sortDirection: sortDirection,
                from,
                to,
                filters: {},
                requestSource: 'filter'
            })
        );

        if (fetchChatHistory.fulfilled.match(resultAction)) {
            dispatch(setChatHistoryCurrentPage(1));
            dispatch(setChatHistoryFilterParams({}));
            dispatch(setChatHistoryDateLabelKey('last30Days'));
        }
        // Announce reset for screen readers
        setResetAnnouncement('Filters reset. Click APPLY to update results');
    };

    // Helper to get Date object from filteredValues
    const getDateFromFilter = (key: keyof FilteredValues): Date | undefined => {
        const val = getStringFilterValue(filteredValues[key]);
        if (!val) return undefined;
        // Try to parse as YYYY-MM-DD
        const d = new Date(val);
        return isNaN(d.getTime()) ? undefined : d;
    };

    const startDate = getDateFromFilter('conversationCreatedAt_gte');
    const endDate = getDateFromFilter('conversationCreatedAt_lte');

    const generateChips = (key: keyof FilteredValues) => {
        const values = getArrayFilterValue(filteredValues[key]);
        return values.map((item, index) => {
            const label =
                key === 'conversationFeedback'
                    ? conversationFeedbackOptions.find(
                          (option) => option.value === item
                      )?.label || item
                    : item;

            return (
                <span
                    key={`${item}_${index}`}
                    title={label}
                    style={{display: 'inline-flex'}}
                >
                    <Chip
                        style={{margin: '1px'}}
                        label={label}
                        action
                        actionIcon="cancel"
                        onAction={(e: React.MouseEvent) =>
                            onChipRemove(e, key, item)
                        }
                    />
                </span>
            );
        });
    };

    const onChipRemove = (
        e: React.MouseEvent,
        key: keyof FilteredValues,
        value: string
    ) => {
        e.stopPropagation();
        const existingValues = getArrayFilterValue(filteredValues[key]);
        setFilteredValues({
            ...filteredValues,
            [key]: existingValues.filter((item) => item !== value)
        });
        // Clear input value when chip is removed
        if (key === 'emailId') {
            setEmailIdInputValue('');
            setEmailIdOptions([]);
            setEmailLookupStatus('idle');
        }
    };

    const handleClickFilter = (key: keyof FilteredValues, value: string) => {
        const existingValues = getArrayFilterValue(filteredValues[key]);
        let updatedValues: Array<string> = [];
        const isRemoving = existingValues.includes(value);

        if (isRemoving) {
            updatedValues = existingValues.filter((v) => v !== value);
        } else {
            updatedValues = [...existingValues, value];
        }
        setFilteredValues({
            ...filteredValues,
            [key]: updatedValues
        });

        // Announce selection for screen readers
        if (key === 'emailId') {
            setEmailIdAnnouncement(
                isRemoving
                    ? `Email ID ${value} deselected`
                    : `Email ID ${value} selected`
            );
        } else if (key === 'conversationFeedback') {
            setFeedbackAnnouncement(
                isRemoving
                    ? `Feedback ${value} deselected`
                    : `Feedback ${value} selected`
            );
        }
    };

    const setDateRangeInFilter = (
        start: Date | undefined,
        end: Date | undefined
    ) => {
        const toLocalDateString = (d: Date) => {
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };
        setFilteredValues((prev) => {
            const updated = {...prev};
            if (start) {
                updated['conversationCreatedAt_gte'] = toLocalDateString(start);
            } else {
                delete updated['conversationCreatedAt_gte'];
            }
            if (end) {
                updated['conversationCreatedAt_lte'] = toLocalDateString(end);
            } else {
                delete updated['conversationCreatedAt_lte'];
            }
            return updated;
        });
    };

    const onClickCustomOption = (option: string) => {
        let start: Date | null = null;
        let end: Date | null = null;
        switch (option) {
            case 'previousDay':
                end = new Date();
                end.setDate(end.getDate() - 1);
                start = new Date(end);
                break;
            case 'today':
                start = end = new Date();
                break;
            case 'last7Days':
                end = new Date();
                start = new Date();
                start.setDate(start.getDate() - 6);
                break;
            case 'last14Days':
                end = new Date();
                start = new Date();
                start.setDate(start.getDate() - 13);
                break;
            case 'last30Days':
                end = new Date();
                start = new Date();
                start.setDate(start.getDate() - 29);
                break;
            case 'last60Days':
                end = new Date();
                start = new Date();
                start.setDate(start.getDate() - 59);
                break;
            case 'last90Days':
                end = new Date();
                start = new Date();
                start.setDate(start.getDate() - 89);
                break;
            default:
                break;
        }
        setDateRangeInFilter(
            start === null ? undefined : start,
            end === null ? undefined : end
        );
        setDateLabelKey(option);
        dispatch(setChatHistoryFilterUserInteracted(true));
        datePickerRef.current?.setOpen(false);
    };

    const hasNonDateFilters =
        getArrayFilterValue(filteredValues.conversationFeedback).length > 0 ||
        getArrayFilterValue(filteredValues.emailId).length > 0 ||
        getStringFilterValue(filteredValues.citation) !== '';

    const fromValue = getStringFilterValue(
        filteredValues.conversationCreatedAt_gte
    );
    const toValue = getStringFilterValue(
        filteredValues.conversationCreatedAt_lte
    );
    const hasIncompleteDateRange =
        (fromValue && !toValue) || (!fromValue && toValue);

    const disableResetButton = !(userInteracted || hasNonDateFilters);

    const closeOpenSelects = () => {
        window.dispatchEvent(new Event('panviva-ui-select-close-all'));
    };

    const toggleDatePicker = () => {
        const shouldOpen = !isDatePickerOpen;
        setIsDatePickerOpen(shouldOpen);
        datePickerRef.current?.setOpen(shouldOpen);
    };

    const handleConversationFeedbackEnter = (
        e: React.KeyboardEvent<HTMLElement>
    ) => {
        // Select from panviva-react-ui does not expose onKeyDown, so we handle Enter on a wrapper.
        // We only trigger when focus is on the readonly trigger input to avoid conflicts with
        // internal keyboard handling (e.g. option checkboxes inside the dropdown).
        if (
            e.defaultPrevented ||
            e.key !== 'Enter' ||
            e.altKey ||
            e.ctrlKey ||
            e.metaKey ||
            e.shiftKey
        )
            return;

        const root = e.currentTarget as HTMLElement;
        const activeElement = document.activeElement as HTMLElement | null;
        if (!activeElement || !root.contains(activeElement)) return;

        const isTriggerInput =
            activeElement.tagName === 'INPUT' &&
            (activeElement as HTMLInputElement).readOnly;
        if (!isTriggerInput) return;

        e.preventDefault();
        e.stopPropagation();

        const trigger = root.querySelector(
            'input[readonly],button,[role="button"]'
        ) as HTMLElement | null;
        (trigger ?? root).click();
    };

    const handleApplyKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === 'Tab' && !e.shiftKey) {
            // Loop focus back to first element (close button)
            e.preventDefault();
            const closeButton = filterPanelRef.current?.querySelector(
                '.close-button'
            ) as HTMLButtonElement;
            if (closeButton) {
                closeButton.focus();
            }
        }
    };

    const handleCloseButtonKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Tab' && e.shiftKey) {
            // Loop focus to last element (Apply button)
            e.preventDefault();
            const applyButton = filterPanelRef.current?.querySelector(
                '.apply-button'
            ) as HTMLButtonElement;
            if (applyButton) {
                applyButton.focus();
            }
        }
    };

    const handleFirstElementKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Tab' && e.shiftKey) {
            // This handler is kept for the date picker wrapper but may not be needed anymore
            // since close button is the actual first element
            e.preventDefault();
            const applyButton = filterPanelRef.current?.querySelector(
                '.apply-button'
            ) as HTMLButtonElement;
            if (applyButton) {
                applyButton.focus();
            }
        }
    };

    return (
        <div className="filter-modal-container" ref={filterPanelRef}>
            <div className="filter-modal-title">
                <span className="filter-text">Filter</span>
                <IconButton
                    icon="clear"
                    className="close-button"
                    aria-label="Close filter panel"
                    onClick={() => {
                        setResetAnnouncement('');
                        onClose && onClose(false);
                    }}
                    onKeyDown={handleCloseButtonKeyDown}
                />
            </div>

            <div className="filter-modal-body-container">
                <div
                    onMouseDown={(e) => {
                        e.stopPropagation();
                        closeOpenSelects();
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        closeOpenSelects();
                    }}
                    onPointerDown={(e) => {
                        e.stopPropagation();
                        closeOpenSelects();
                    }}
                    onKeyDown={handleFirstElementKeyDown}
                >
                    <DatePicker
                        ref={datePickerRef}
                        selectsRange
                        dateFormat="dd/MM/yyyy"
                        startDate={
                            startDate === undefined ? undefined : startDate
                        }
                        endDate={endDate === undefined ? undefined : endDate}
                        minDate={(() => {
                            const d = new Date();
                            d.setDate(d.getDate() - 89);
                            return d;
                        })()}
                        maxDate={new Date()}
                        onChange={(dates: any) => {
                            const [start, end] = dates;
                            setDateRangeInFilter(
                                start === null ? undefined : start,
                                end === null ? undefined : end
                            );
                            setDateLabelKey(null);
                            dispatch(setChatHistoryFilterUserInteracted(true));
                        }}
                        onCalendarOpen={() => setIsDatePickerOpen(true)}
                        onCalendarClose={() => setIsDatePickerOpen(false)}
                        customInput={
                            <CustomDateInput
                                isOpen={isDatePickerOpen}
                                onToggle={toggleDatePicker}
                                displayValue={
                                    dateLabelKey
                                        ? DATE_PRESET_OPTIONS.find(
                                              (o) => o.value === dateLabelKey
                                          )?.label
                                        : undefined
                                }
                            />
                        }
                        calendarContainer={(containerProps: any) => (
                            <CustomDateContainer
                                {...containerProps}
                                onClickCustomOption={onClickCustomOption}
                            />
                        )}
                        dropdownMode="select"
                        placeholderText="Select date range"
                    />
                </div>

                <div
                    ref={emailWrapperRef}
                    className="filter-select-wrapper"
                    onInput={handleEmailIdType}
                    onClick={(e) => {
                        e.stopPropagation();
                        const target = e.target as HTMLElement;
                        if (
                            !emailSelectOpen.current &&
                            target.tagName !== 'INPUT'
                        ) {
                            const input = (
                                e.currentTarget as HTMLElement
                            ).querySelector<HTMLInputElement>(
                                'input[data-testid="textfield-input"]'
                            );
                            input?.click();
                        }
                    }}
                    onBlur={() => {
                        if (!isSelectingEmailId.current) {
                            setEmailIdInputValue('');
                            setEmailIdOptions([]);
                            setEmailLookupStatus('idle');
                        }
                        isSelectingEmailId.current = false;
                    }}
                >
                    {/* Hidden description for screen readers */}
                    <span
                        id="email-id-description"
                        style={{
                            position: 'fixed',
                            left: '-9999px',
                            width: '1px',
                            height: '1px',
                            overflow: 'hidden'
                        }}
                    >
                        {getArrayFilterValue(filteredValues.emailId).length > 0
                            ? `${getArrayFilterValue(filteredValues.emailId).length} email IDs selected: ${getArrayFilterValue(filteredValues.emailId).join(', ')}`
                            : 'No email IDs selected. Type to search and select email IDs'}
                    </span>
                    <Select
                        label="Email Id"
                        dropdownClass="filter-select-container"
                        enableType
                        multiple
                        closeOnSelect={false}
                        selected={getArrayFilterValue(filteredValues.emailId)}
                        chips={generateChips('emailId') || []}
                        limitTags={2}
                        inputValue={emailIdInputValue}
                        onOpen={() => {
                            emailSelectOpen.current = true;
                            setTimeout(() => {
                                emailWrapperRef.current
                                    ?.querySelector<HTMLInputElement>(
                                        'input[data-testid="textfield-input"]'
                                    )
                                    ?.focus();
                            }, 0);
                        }}
                        onClose={() => {
                            emailSelectOpen.current = false;
                            setEmailIdOptions([]);
                            setEmailIdInputValue('');
                            setEmailLookupStatus('idle');
                        }}
                    >
                        {emailIdOptions?.length > 0 ? (
                            emailIdOptions.map((option: string) => {
                                const isSelected = getArrayFilterValue(
                                    filteredValues.emailId
                                ).includes(option);
                                return (
                                    <div
                                        key={option}
                                        className="filter-select-items"
                                        role="menuitemcheckbox"
                                        aria-checked={isSelected}
                                        tabIndex={0}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            isSelectingEmailId.current = true;
                                        }}
                                        onClick={() => {
                                            handleClickFilter(
                                                'emailId',
                                                option
                                            );
                                            setEmailIdInputValue('');
                                        }}
                                        onKeyDown={(e) => {
                                            if (
                                                e.key === 'Enter' ||
                                                e.key === ' '
                                            ) {
                                                e.preventDefault();
                                                handleClickFilter(
                                                    'emailId',
                                                    option
                                                );
                                                setEmailIdInputValue('');
                                            }
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            readOnly
                                            aria-hidden="true"
                                        />
                                        <span>{option}</span>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="filter-select-tip">
                                {getLookupTipText(
                                    emailLookupStatus,
                                    'Search email',
                                    'emailId'
                                )}
                            </div>
                        )}
                    </Select>
                </div>

                <div
                    ref={citationWrapperRef}
                    className="filter-select-wrapper"
                    onInput={handleCitationType}
                    onClick={(e) => {
                        const target = e.target as HTMLElement;
                        if (!target.closest('.filter-select-container')) {
                            e.stopPropagation();
                        }
                    }}
                    onKeyDown={(e) => {
                        // Prevent non-numeric characters from being entered
                        const allowedKeys = [
                            'Backspace',
                            'Delete',
                            'Tab',
                            'ArrowLeft',
                            'ArrowRight',
                            'ArrowUp',
                            'ArrowDown',
                            'Enter',
                            'Escape'
                        ];
                        if (
                            !allowedKeys.includes(e.key) &&
                            !/^\d$/.test(e.key)
                        ) {
                            e.preventDefault();
                        }
                    }}
                    onBlur={() => {
                        if (!isSelectingCitation.current) {
                            const selectedCitation = getStringFilterValue(
                                filteredValues.citation
                            );
                            if (!selectedCitation) {
                                setCitationInputValue('');
                                setCitationOptions([]);
                                setCitationLookupStatus('idle');
                            }
                        }
                        isSelectingCitation.current = false;
                    }}
                >
                    <Select
                        label="Citation"
                        dropdownClass="filter-select-container"
                        enableType
                        closeOnSelect={false}
                        selected={
                            getStringFilterValue(filteredValues.citation)
                                ? [
                                      getStringFilterValue(
                                          filteredValues.citation
                                      )
                                  ]
                                : []
                        }
                        inputValue={citationInputValue}
                        onOpen={() => {
                            setTimeout(() => {
                                citationWrapperRef.current
                                    ?.querySelector<HTMLInputElement>(
                                        'input[data-testid="textfield-input"]'
                                    )
                                    ?.focus();
                            }, 0);
                        }}
                        onClose={() => {
                            if (!citationInputValue) {
                                setCitationOptions([]);
                                setCitationLookupStatus('idle');
                            }
                            isSelectingCitation.current = false;
                        }}
                    >
                        {citationOptions?.length > 0 ? (
                            citationOptions.map((option: string) => {
                                return (
                                    <div
                                        key={option}
                                        className="filter-select-items"
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            isSelectingCitation.current = true;
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setFilteredValues((prev) => ({
                                                ...prev,
                                                citation: option
                                            }));
                                            skipCitationFetch.current = true;
                                            setCitationInputValue(option);
                                            setCitationOptions([]);
                                            setCitationLookupStatus('idle');
                                        }}
                                    >
                                        <span>{option}</span>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="filter-select-tip">
                                {getLookupTipText(
                                    citationLookupStatus,
                                    'Search citation',
                                    'citation'
                                )}
                            </div>
                        )}
                    </Select>
                </div>

                <div
                    ref={conversationFeedbackWrapperRef}
                    className="filter-select-wrapper"
                    data-testid="conversation-feedback-keyboard-wrapper"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={handleConversationFeedbackEnter}
                >
                    {/* Hidden description for screen readers */}
                    <span
                        id="feedback-description"
                        style={{
                            position: 'fixed',
                            left: '-9999px',
                            width: '1px',
                            height: '1px',
                            overflow: 'hidden'
                        }}
                    >
                        {getArrayFilterValue(
                            filteredValues.conversationFeedback
                        ).length > 0
                            ? `${getArrayFilterValue(filteredValues.conversationFeedback).length} feedback values selected: ${getArrayFilterValue(filteredValues.conversationFeedback).join(', ')}`
                            : 'No feedback values selected. Press Enter to open and select feedback'}
                    </span>
                    <Select
                        label="Conversation Feedback"
                        dropdownClass="filter-select-container"
                        multiple
                        closeOnSelect={false}
                        selected={getArrayFilterValue(
                            filteredValues.conversationFeedback
                        )}
                        chips={generateChips('conversationFeedback') || []}
                        limitTags={2}
                        onOpen={() => {
                            setTimeout(() => {
                                conversationFeedbackWrapperRef.current
                                    ?.querySelector<HTMLInputElement>(
                                        'input[data-testid="textfield-input"]'
                                    )
                                    ?.focus();
                            }, 0);
                        }}
                    >
                        {conversationFeedbackOptions.map(
                            (option: any, index: number) => {
                                const isSelected = getArrayFilterValue(
                                    filteredValues.conversationFeedback
                                ).includes(option.value);
                                return (
                                    <div
                                        key={option.value}
                                        className="filter-select-items"
                                        role="menuitemcheckbox"
                                        aria-checked={isSelected}
                                        tabIndex={0}
                                        onClick={() =>
                                            handleClickFilter(
                                                'conversationFeedback',
                                                option.value
                                            )
                                        }
                                        onKeyDown={(e) => {
                                            if (
                                                e.key === 'Enter' ||
                                                e.key === ' '
                                            ) {
                                                e.preventDefault();
                                                handleClickFilter(
                                                    'conversationFeedback',
                                                    option.value
                                                );
                                            }
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            readOnly
                                            aria-hidden="true"
                                        />
                                        <span>{option.label}</span>
                                    </div>
                                );
                            }
                        )}
                    </Select>
                </div>
            </div>

            <div className="filter-modal-footer">
                <Button
                    className="reset-button"
                    variant="text"
                    onClick={handleReset}
                    disabled={disableResetButton}
                >
                    reset
                </Button>
                &nbsp;
                <Button
                    className="apply-button"
                    variant="text"
                    onClick={handleApply}
                    onKeyDown={handleApplyKeyDown}
                    disabled={Boolean(hasIncompleteDateRange)}
                >
                    apply
                </Button>
            </div>

            {/* Screen reader only announcement for reset */}
            <div
                role="status"
                aria-live="polite"
                aria-atomic="true"
                style={{
                    position: 'fixed',
                    left: '-9999px',
                    width: '1px',
                    height: '1px',
                    overflow: 'hidden'
                }}
            >
                {resetAnnouncement}
            </div>

            {/* Screen reader only announcement for Email ID filter */}
            <div
                role="status"
                aria-live="polite"
                aria-atomic="true"
                style={{
                    position: 'fixed',
                    left: '-9999px',
                    width: '1px',
                    height: '1px',
                    overflow: 'hidden'
                }}
            >
                {emailIdAnnouncement}
            </div>

            {/* Screen reader only announcement for Feedback filter */}
            <div
                role="status"
                aria-live="polite"
                aria-atomic="true"
                style={{
                    position: 'fixed',
                    left: '-9999px',
                    width: '1px',
                    height: '1px',
                    overflow: 'hidden'
                }}
            >
                {feedbackAnnouncement}
            </div>
        </div>
    );
};

export default FilterPanel;

